if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product') || document.querySelector('.cc-main-product');
      
      this.productInfo = this.productSection.querySelector('.product-info');
      this.addToCartButton = this.productInfo.querySelector('.product-info__add-button');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.addToCartButton && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.addToCartButton && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.addToCartButton) observer.observe(this.addToCartButton);
      if (this.productInfo) observer.observe(this.productInfo);

      // Handle sticky ATC button click
      const stickyAtcBtn = this.querySelector('.sticky-atc-btn');
      const mainSubmitBtn = this.productForm ? this.productForm.querySelector('[name="add"]') : null;
      
      if (stickyAtcBtn && this.productForm && mainSubmitBtn) {
        // Sync sticky button state, text, and color with main button
        const syncButtonStates = () => {
          // Sync loading state
          if (mainSubmitBtn.classList.contains('is-loading')) {
            stickyAtcBtn.setAttribute('aria-disabled', 'true');
            stickyAtcBtn.classList.add('is-loading');
          } else {
            stickyAtcBtn.removeAttribute('aria-disabled');
            stickyAtcBtn.classList.remove('is-loading');
          }
          
          // Sync success state
          if (mainSubmitBtn.classList.contains('is-success')) {
            stickyAtcBtn.classList.add('is-success');
          } else {
            stickyAtcBtn.classList.remove('is-success');
          }
          
          // Sync disabled state
          if (mainSubmitBtn.disabled || mainSubmitBtn.getAttribute('aria-disabled') === 'true') {
            stickyAtcBtn.disabled = true;
            stickyAtcBtn.setAttribute('aria-disabled', 'true');
          } else {
            stickyAtcBtn.disabled = false;
            stickyAtcBtn.removeAttribute('aria-disabled');
          }
          
          // Sync button text content (handle buttons with child elements or value attribute)
          const getButtonText = (btn) => {
            // Check if button uses value attribute (for submit buttons)
            if (btn.value && btn.value.trim()) {
              return btn.value.trim();
            }
            // Otherwise get text content (handles buttons with spans/other elements)
            return btn.textContent.trim();
          };
          
          const mainButtonText = getButtonText(mainSubmitBtn);
          const stickyButtonText = getButtonText(stickyAtcBtn);
          
          if (mainButtonText && mainButtonText !== stickyButtonText) {
            // Update both textContent and value if it's a button
            stickyAtcBtn.textContent = mainButtonText;
            if (stickyAtcBtn.value !== undefined) {
              stickyAtcBtn.value = mainButtonText;
            }
          }
          
          // Sync button color/class (btn--primary vs btn--secondary)
          const mainHasPrimary = mainSubmitBtn.classList.contains('btn--primary');
          const mainHasSecondary = mainSubmitBtn.classList.contains('btn--secondary');
          const stickyHasPrimary = stickyAtcBtn.classList.contains('btn--primary');
          const stickyHasSecondary = stickyAtcBtn.classList.contains('btn--secondary');
          
          if (mainHasPrimary && !stickyHasPrimary) {
            stickyAtcBtn.classList.remove('btn--secondary');
            stickyAtcBtn.classList.add('btn--primary');
          } else if (mainHasSecondary && !stickyHasSecondary) {
            stickyAtcBtn.classList.remove('btn--primary');
            stickyAtcBtn.classList.add('btn--secondary');
          }
          
          // Sync data-add-to-cart-text attribute if it exists
          if (mainSubmitBtn.dataset.addToCartText) {
            stickyAtcBtn.dataset.addToCartText = mainSubmitBtn.dataset.addToCartText;
          }
        };
        
        // Watch for changes to main button (text, classes, attributes, disabled state)
        const observer = new MutationObserver(syncButtonStates);
        if (mainSubmitBtn) {
          observer.observe(mainSubmitBtn, {
            attributes: true,
            attributeFilter: ['class', 'aria-disabled', 'disabled', 'data-add-to-cart-text','value'],
            childList: true,
            characterData: true,
            subtree: true
          });
        }
        
        // Also watch for input events that might change the button (e.g., variant changes)
        const form = this.productForm.querySelector('.js-product-form');
        if (form) {
          form.addEventListener('change', syncButtonStates);
          form.addEventListener('input', syncButtonStates);
        }
        
        // Listen for variant change events that might update the button
        document.addEventListener('on:variant:change', () => {
          // Small delay to allow button to update first
          setTimeout(syncButtonStates, 1000000);
        });

        // Initial sync
        syncButtonStates();
        
        // Handle sticky button click
        stickyAtcBtn.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Prevent double submission
          if (stickyAtcBtn.getAttribute('aria-disabled') === 'true') return;
          
          const form = this.productForm.querySelector('.js-product-form');
          if (form && mainSubmitBtn) {
            const variantId = stickyAtcBtn.dataset.stickyAtcVariantId;
            const variantInput = form.querySelector('[name="id"]');
            if (variantInput && variantId) {
              variantInput.value = variantId;
              variantInput.disabled = false;
            }
            
            // Trigger main button click to use the same submission flow
            if (!mainSubmitBtn.disabled && mainSubmitBtn.getAttribute('aria-disabled') !== 'true') {
              mainSubmitBtn.click();
            }
          }
        });
      }

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      // Update sticky panel title and price on variant change
      document.addEventListener('on:variant:change', (evt) => {
        // Only respond to events from this product section
        if (!this.productSection.contains(evt.target)) return;

        const { variant, product } = evt.detail;
        if (!variant) return;

        // Update image to match selected variant
        const mediaDiv = this.querySelector('[data-sticky-atc-media]');
        if (mediaDiv) {
          const img = mediaDiv.querySelector('img');
          const imageSrc = variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src;
          if (img && imageSrc) {
            const base = imageSrc.includes('?') ? imageSrc : `${imageSrc}?v=1`;
            const src1x = `${base}&width=56`;
            const src2x = `${base}&width=112`;
            img.srcset = `${src1x}, ${src2x} 2x`;
            img.src = src2x;
            img.dataset.src = src2x;
            img.dataset.srcset = `${src1x}, ${src2x} 2x`;
          }
        }

        // Update title: show "Product Title - Variant Title" unless it's the only/default variant
        const titleEl = this.querySelector('[data-sticky-atc-title]');
        if (titleEl) {
          let title = product.title;
          const isCombinedListing = this.dataset.isCombinedListing === 'true';
          if (!product.has_only_default_variant && variant.title && variant.title !== 'Default Title' && !isCombinedListing) {
            title = `${product.title} - ${variant.title}`;
          }
          // Truncate to 70 chars to match the Liquid output
          titleEl.textContent = title.length > 70 ? `${title.substring(0, 67)}...` : title;
        }

        // Show ATC button and hide "choose options" link once a variant is selected
        const stickyBtn = this.querySelector('.sticky-atc-btn');
        const selectLink = this.querySelector('[data-sticky-select-link]');
        if (stickyBtn && selectLink) {
          stickyBtn.dataset.stickyAtcVariantId = variant.id;
          stickyBtn.hidden = false;
          selectLink.hidden = true;
        } else if (stickyBtn) {
          // Single-variant: keep variant ID in sync
          stickyBtn.dataset.stickyAtcVariantId = variant.id;
        }

        // Update price: read formatted price from the variant-picker JSON data
        const priceWrapEl = this.querySelector('[data-sticky-atc-price]');
        if (priceWrapEl) {
          const variantPicker = this.productSection.querySelector('variant-picker');
          const dataEl = variantPicker && variantPicker.querySelector('[type="application/json"]');
          if (dataEl) {
            const pickerData = JSON.parse(dataEl.textContent);
            const formatted = pickerData.formatted && pickerData.formatted[variant.id];
            if (formatted) {
              const priceCurrentEl = priceWrapEl.querySelector('.price__current.money');
              const priceFromEl = priceWrapEl.querySelector('.price__from');
              const priceEl = priceWrapEl.querySelector('.price');

              if (priceCurrentEl) priceCurrentEl.innerHTML = formatted.price;
              // Hide the "from" label now that a specific variant is selected
              if (priceFromEl) priceFromEl.hidden = true;
              // Toggle sale state
              if (priceEl) {
                priceEl.classList.toggle('price--on-sale', !!formatted.compareAtPrice);
                priceEl.classList.toggle('price--sold-out', !variant.available);
              }
            }
          }
        }
      });
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}